<template>
    <vx-table-row class="relative p-4! sm:pl-6!" :disabled="!props.item.value" data-test="blast-forecast-row">
        <div
            class="w-2 sm:w-4 min-h-[30px] rounded-full absolute left-4 top-4 bottom-4 border-0! sm:left-6"
            :class="!props.item.value ? 'bg-slate-200' : chartCalendarIntensities[props.item.intensity]"
        ></div>

        <vx-table-row-item class="ml-6 sm:ml-0 sm:order-2">
            <template v-slot:label>Date: </template>
            <div
                class="bg-colors-slate-50 rounded-[3px] shadow-[0px_1px_2px_0px_rgba(30,58,138,0.08)] outline-[0.50px] outline-offset-[-0.50px] outline-slate-500/40 flex sm:flex-col justify-start items-center overflow-hidden sm:h-6 sm:min-w-[22px]"
            >
                <div class="py-1.5 px-1 w-full bg-slate-500 flex justify-center items-center gap-2 sm:py-[3px]">
                    <div
                        class="text-center justify-center text-white text-[6px] font-medium uppercase leading-[6px]"
                        data-test="date-month"
                    >
                        {{ date.month }}
                    </div>
                </div>
                <div class="p-1 flex justify-center items-center gap-2 sm:p-0">
                    <div
                        class="text-center justify-start text-slate-800 text-[10px] font-bold leading-none"
                        data-test="date-day"
                    >
                        {{ date.day }}
                    </div>
                </div>
            </div>

            <div v-if="date.today" class="font-bold leading-none ml-2 block sm:hidden md:block">Today</div>
        </vx-table-row-item>

        <vx-table-row-item class="ml-6 sm:ml-7 sm:order-1">
            <template v-slot:label>Messages: </template>
            <vx-popover
                class="cursor-pointer"
                arrow
                placement="top"
                @click="copyText(props.item.num_messages)"
                hover
                data-test="message-count"
            >
                {{ vxFormatCount(props.item.num_messages) }}
                <template v-slot:content>
                    <div class="text-center" @click="copyText(props.item.num_messages)">
                        {{ Intl.NumberFormat('en-US').format(props.item.num_messages) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups" class="ml-6 order-3 sm:ml-0">
            <template v-slot:label>Senders: </template>
            <div class="flex gap-1">
                <meta-badge
                    v-if="props.item.num_corporate > 0"
                    :icon="faCrown"
                    color="amber"
                    data-test="corporate-badge"
                >
                    {{ props.item.num_corporate }}
                </meta-badge>
                <meta-badge
                    v-if="props.item.num_org_units > 0"
                    :icon="faBuildingFlag"
                    color="violet"
                    data-test="org-units-badge"
                >
                    {{ props.item.num_org_units }}
                </meta-badge>
                <meta-badge v-if="props.item.num_groups > 0" :icon="faGrid2Plus" color="slate" data-test="groups-badge">
                    {{ props.item.num_groups }}
                </meta-badge>
            </div>
        </vx-table-row-item>

        <vx-table-row-item class="ml-6 order-4 sm:ml-0">
            <template v-slot:label>Blasts: </template>
            <vx-popover
                class="cursor-pointer"
                arrow
                placement="top"
                @click="copyText(props.item.num_blasts)"
                hover
                data-test="blast-count"
            >
                <div>{{ vxFormatCount(props.item.num_blasts) }} <span class="hidden sm:inline-block">blasts</span></div>
                <template v-slot:content>
                    <div class="text-center" @click="copyText(props.item.num_blasts)">
                        {{ Intl.NumberFormat('en-US').format(props.item.num_blasts) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <div class="flex items-center justify-end gap-2 col-span-full sm:col-auto order-5">
            <vx-button
                color="muted-light"
                class="w-full ml-6 sm:ml-0 sm:w-auto"
                :outline="showCampaigns"
                :disabled="!props.item.value"
                size="sm"
                data-test="details-btn"
                @mouseenter="!showCampaigns && fetchCampaigns()"
                @click.stop="toggleCampaigns()"
            >
                Details
                <font-awesome-icon
                    :icon="faCaretDown"
                    class="w-3 h-3 ml-1 transition-transform"
                    :class="{ 'rotate-180': showCampaigns }"
                />
            </vx-button>
        </div>
    </vx-table-row>

    <div class="pl-12 sm:pl-16 relative col-span-full py-1">
        <animate-height :height="showCampaigns ? 'auto' : 0" :animate-opacity="false">
            <div
                class="border-0! w-px absolute top-0 left-5 ml-px sm:ml-0 sm:left-8"
                :class="[
                    chartCalendarIntensities[props.item.intensity],
                    {
                        'h-full': props.nextItem,
                        'h-10': !props.nextItem,
                        'opacity-0': !nextItem && !showCampaigns,
                        transition: props.nextItem,
                    },
                ]"
            ></div>

            <div class="py-1">
                <vx-alert v-if="errorCampaigns" color="danger" :closeable="false" shadow>
                    Failed to load data
                    <template v-slot:right>
                        <vx-button @click="fetchCampaigns" :loading="loadingCampaigns" size="sm" color="danger"
                            >Retry<font-awesome-icon widthAuto :icon="faRefresh"
                        /></vx-button>
                    </template>
                </vx-alert>
                <div v-else-if="loadingCampaigns" class="flex justify-center py-10">
                    <vx-icon-loading class="w-5 h-5" spin />
                </div>
                <div v-else class="flex flex-col gap-4">
                    <div v-for="campaign in campaigns" :key="campaign.id" class="relative">
                        <div
                            class="w-5 h-5 -mb-5 translate-y-9 -translate-x-[37px] top-10 rounded-full border-4! border-solid border-slate-200! sm:-translate-x-[42px]"
                            :class="[chartCalendarIntensities[props.item.intensity]]"
                        ></div>

                        <blast-forecast-campaign-detail
                            :campaign="campaign"
                            @deleted="removeCampaign($event)"
                            @statusChanged="updateCampaignStatus"
                            :timezone="timezone"
                            :orgUnitId="props.orgUnitId"
                        />
                    </div>
                </div>
            </div>
        </animate-height>
    </div>
</template>

<script setup lang="ts">
import {
    VxTableRow,
    VxButton,
    VxIconLoading,
    vxFormatCount,
    chartCalendarIntensities,
    VxTableRowItem,
    VxPopover,
    VxAlert,
} from '@voxie/frontend-components';
import BlastForecastCampaignDetail from './BlastForecastCampaignDetail.vue';
import MetaBadge from '~/components/general/MetaBadge.vue';
import AnimateHeight from 'vue-animate-height';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCrown, faBuildingFlag, faGrid2Plus, faCaretDown, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import type {
    BlastForecastCampaignsResponse,
    GetBlastForecastRequest,
} from '~/services/blastForecast/blastForecastClient.types';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import { computed, ref, watch, type PropType, inject } from 'vue';
import type { BlastForecastItemAggregated } from '../composables/useBlastForecastSummary';
import { useCopyText } from '~/composables/useCopyText';
import type { Features } from '~/provides/features.types';
import type { CampaignStatus } from '~/constants/campaign';

const FEATURES = inject('FEATURES') as Features;

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    item: {
        type: Object as PropType<BlastForecastItemAggregated>,
        required: true,
    },
    timezone: String,
    fetchParams: {
        type: Object as PropType<GetBlastForecastRequest>,
        required: true,
    },
    nextItem: Object as PropType<BlastForecastItemAggregated>,
    orgUnitId: Number,
});

const { copyText } = useCopyText();

const showCampaigns = ref(false);
const campaigns = ref<BlastForecastCampaignsResponse['items']>([]);

const removeCampaign = (campaignId: number) => {
    campaigns.value = campaigns.value.filter((campaign) => campaign.id != campaignId);
};

const updateCampaignStatus = (campaignId: number, newStatus: CampaignStatus) => {
    const campaign = campaigns.value.find((campaign) => campaign.id === campaignId);
    if (campaign) {
        campaign.status = newStatus;
    }
};

const errorCampaigns = ref(false);
const loadingCampaigns = ref(false);

let lastFetchedAt: dayjs.Dayjs | null = null;

const fetchCampaigns = async () => {
    if (loadingCampaigns.value) {
        return;
    }

    const now = dayjs();
    const oneMinuteAgo = now.subtract(1, 'minute');

    // prevents multiple api calls on mouseenter/multiple clicks
    if (!errorCampaigns.value && lastFetchedAt && lastFetchedAt.isAfter(oneMinuteAgo)) {
        return;
    }

    lastFetchedAt = now;

    loadingCampaigns.value = true;

    const dayStart = toUTCDateTimeFormat(dayjs.utc(props.item.date).tz(props.timezone).startOf('day'));
    const dayEnd = toUTCDateTimeFormat(
        dayjs.utc(props.item.date).tz(props.timezone).startOf('day').add(1, 'day').startOf('hour')
    );

    const dayParams: GetBlastForecastRequest = {
        ...props.fetchParams,
        range: [dayStart, dayEnd],
    };

    try {
        const response = await blastForecastClient.getBlastForecastCampaigns(
            { teamId, orgUnitId: props.orgUnitId },
            dayParams
        );
        campaigns.value = response.data.items;
        errorCampaigns.value = false;
    } catch (error) {
        console.error(`Error loading campaigns for ${props.item.date}:`, error);
        campaigns.value = [];
        errorCampaigns.value = true;
    } finally {
        loadingCampaigns.value = false;
    }
};

watch(showCampaigns, () => {
    if (showCampaigns.value) {
        fetchCampaigns();
    }
});

watch(
    () => props.fetchParams,
    () => {
        // closes day and clear the "cache" flag, so when day is opened the data is fetched again
        lastFetchedAt = null;
        showCampaigns.value = false;
    },
    {
        deep: true,
    }
);

const date = computed(() => {
    const dayjsInstance = dayjs.utc(props.item.date).tz(props.timezone);

    return {
        month: dayjsInstance.format('MMM'),
        day: dayjsInstance.format('D'),
        today: dayjsInstance.isToday(),
    };
});

const toggleCampaigns = () => {
    if (!props.item.value) {
        return;
    }

    showCampaigns.value = !showCampaigns.value;
};

const openCampaigns = () => {
    if (!props.item.value) {
        return;
    }

    showCampaigns.value = true;
};

const closeCampaigns = () => {
    if (!props.item.value) {
        return;
    }

    showCampaigns.value = false;
};

defineExpose({
    toggleCampaigns,
    openCampaigns,
    closeCampaigns,
});
</script>
